import { useCallback } from 'react';

import { Box } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import useOnIntersect from '@rover/react-lib/src/hooks/useOnIntersect';

import { GALLERY_CONTAINER_WIDTH, GALLERY_IMAGE_SIZE } from './constants';
import type { GalleryImage, PaginationConfig } from './ImageGallery.common';
import ImageWithShimmer from './ImageWithShimmer';
import { getShimmerCount, shouldLoadMore } from './utils';

type ImageGridContainerProps = {
  images: GalleryImage[];
  onImageClick: (index: number) => void;
  // Mobile only props
  // eslint-disable-next-line react/no-unused-prop-types
  onClose?: () => void;
  pagination?: PaginationConfig;
};

const SHIMMER_SIZE = parseInt(GALLERY_IMAGE_SIZE, 10);

const ImageGridContainer = ({
  images,
  onImageClick,
  pagination,
}: ImageGridContainerProps): JSX.Element | null => {
  const handleLoadMore = useCallback(() => {
    if (shouldLoadMore(pagination)) {
      pagination?.onLoadMore();
    }
  }, [pagination]);

  const sentinelRef = useOnIntersect(handleLoadMore, false);

  const shimmerCount = getShimmerCount(pagination, images.length);

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, ${GALLERY_IMAGE_SIZE})`,
          gridAutoRows: GALLERY_IMAGE_SIZE,
          rowGap: '4x',
          columnGap: '3x',
          margin: '0 auto',
          justifyContent: 'center',
          width: '100%',
          maxWidth: GALLERY_CONTAINER_WIDTH,
        }}
      >
        {images.map((image, index) => (
          <ImageWithShimmer
            src={image.small}
            caption={image.caption}
            key={image.small}
            onClick={() => onImageClick(index)}
          />
        ))}
        {pagination?.isLoadingMore &&
          Array.from({ length: shimmerCount }, (_, i) => (
            <LoadingShimmer
              key={`shimmer-${i}`}
              width={SHIMMER_SIZE}
              height={SHIMMER_SIZE}
              borderRadius="primary"
              m="0x"
              data-testid="loading-more-shimmer"
            />
          ))}
      </Box>
      {pagination?.hasMore && (
        <Box ref={sentinelRef} height="1px" aria-hidden="true" data-testid="scroll-sentinel" />
      )}
    </>
  );
};

export default ImageGridContainer;
